import React, { useState } from 'react'

import { Button, Dialog, Icon } from 'bs-unified-ui'
import {
  Box,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { COMMENT_STATUS } from '~/constants/comments'
import { dialogParams } from '~/components/common/Dialog/CustomDialog'
import { showDialog } from '~/components/common/Dialog'
import commentApi from '~/services/apis/comment'
import { showToast } from '~/components/common/Toast'

interface BulkActionsProps {
  selected?: string[]
  clearSelected?: () => void
  refetchData?: () => void
  status?: string
  loading?: boolean
  setLoading?: (loading: boolean) => void
}

export const BulkActions: React.FC<BulkActionsProps> = ({
  selected,
  clearSelected,
  status,
  loading,
  setLoading,
  refetchData,
}) => {
  const theme: Theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down(1024))
  const [dialog, setDialog] = useState<'trash' | 'delete' | ''>('')

  const bulkUpdate = async (newStatus) => {
    try {
      setLoading(true)
      await commentApi.bulkUpdate(selected, newStatus)
      showToast('Changes saved')
      refetchData()
    } catch (e) {
      showDialog(dialogParams.error(e))
    } finally {
      setDialog('')
      setLoading(false)
    }
  }

  const bulkDelete = async () => {
    try {
      setLoading(true)
      await commentApi.bulkDelete(selected)
      showToast('Changes saved')
      refetchData()
    } catch (e) {
      showDialog(dialogParams.error(e))
    } finally {
      setDialog('')
      setLoading(false)
    }
  }

  return (
    <>
      <Dialog
        open={!!selected.length}
        maxWidth="min(1184px, 70vw)"
        placement="bottom"
        bgColorContent={theme.foreground.black}
        hideBackdrop
        disableEnforceFocus
        style={{ position: 'initial' }}
        disableBackdropClick
        disableAutoFocus
        sx={{
          ...(isTablet ? {} : { marginLeft: 32 }),
          '& .MuiPaper-root': { m: 2 },
          '& .MuiDialogContent-root': {
            paddingLeft: 3,
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Box>
            <Icon
              name="checkbox-multiple"
              type="fill"
              isCustom={false}
              size={16}
              color={theme.foreground.white}
            />
            <Typography
              variant="fs16"
              sx={{ ml: 1 }}
              color={theme.foreground.white}
            >
              {selected.length} {selected.length === 1 ? 'comment' : 'comments'}{' '}
              selected
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={2}
            sx={loading ? { pointerEvents: 'none' } : {}}
          >
            {status === COMMENT_STATUS.PENDING && (
              <>
                <Button
                  startIconName="check"
                  onClick={() => bulkUpdate(COMMENT_STATUS.APPROVED)}
                >
                  Publish
                </Button>
                <Button
                  color="warning"
                  startIconName="spam-3"
                  onClick={() => bulkUpdate(COMMENT_STATUS.SPAM)}
                >
                  Spam
                </Button>
                <Button
                  color="error"
                  startIconName="delete-bin"
                  onClick={() => setDialog('trash')}
                >
                  Move to trash
                </Button>
              </>
            )}

            {status === COMMENT_STATUS.APPROVED && (
              <>
                <Button onClick={() => bulkUpdate(COMMENT_STATUS.PENDING)}>
                  Unpublish
                </Button>
                <Button
                  color="warning"
                  startIconName="spam-3"
                  onClick={() => bulkUpdate(COMMENT_STATUS.SPAM)}
                >
                  Spam
                </Button>
                <Button
                  color="error"
                  startIconName="delete-bin"
                  onClick={() => setDialog('trash')}
                >
                  Move to trash
                </Button>
              </>
            )}
            {status === COMMENT_STATUS.SPAM && (
              <>
                <Button onClick={() => bulkUpdate(COMMENT_STATUS.PENDING)}>
                  Move to review
                </Button>
                <Button
                  color="error"
                  startIconName="delete-bin"
                  onClick={() => setDialog('trash')}
                >
                  Move to trash
                </Button>
              </>
            )}
            {status === COMMENT_STATUS.TRASH && (
              <>
                <Button onClick={() => bulkUpdate(COMMENT_STATUS.PENDING)}>
                  Restore
                </Button>
                <Button
                  color="error"
                  startIconName="delete-bin"
                  onClick={() => setDialog('delete')}
                >
                  Delete permanently
                </Button>
              </>
            )}

            <Button
              sx={{
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: '#ffffff1a',
                },
              }}
              onClick={clearSelected}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Dialog>

      <Dialog
        title="Move to trash"
        maxWidth={336}
        open={dialog === 'trash'}
        onClose={() => setDialog('')}
        renderFooter={
          <>
            <Button variant="text" onClick={() => setDialog('')}>
              Cancel
            </Button>
            <Button
              color="error"
              onClick={() => bulkUpdate(COMMENT_STATUS.TRASH)}
              loading={loading}
            >
              Move to trash
            </Button>
          </>
        }
      >
        <Typography variant="fs14">
          If you move these comments to trash, you can still recover it in the
          "Trash" view.
        </Typography>
      </Dialog>

      <Dialog
        title="Delete permanently"
        maxWidth={336}
        open={dialog === 'delete'}
        onClose={() => setDialog('')}
        renderFooter={
          <>
            <Button variant="text" onClick={() => setDialog('')}>
              Cancel
            </Button>
            <Button color="error" onClick={bulkDelete} loading={loading}>
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="fs14">
          Once deleted, comments cannot be recovered,
          <br />
          Are you sure you want to continue?
        </Typography>
      </Dialog>
    </>
  )
}
